import { RawPageData, SFFProblemType } from 'its-pages';
import { Competencies, StatisticsTagsByCompetency } from '../redux/reducers/statistics';
import { ConfusionCardElement, IssueCardElement } from '../types/cards';

export const tourExampleCompetencies: Competencies = {
  "Assessing Suicide Risk": {
    "id": "Assessing Suicide Risk",
    "name": "Assessing Suicide Risk",
    "content": {
        "text": "When patients endorse item 9 on the PHQ or when they report any thoughts of death or suicide, the provider must follow up with a series of questions.  These questions largely focus on suicidal thoughts in the past month, with some question focused on suicidal behavior over the patient's lifetime.   Question to determine suicide risk must be asked in a prescribed order."
    }
  }} as Competencies;

export const tourExampleTagsByCompetency: StatisticsTagsByCompetency = {
    "Assessing Suicide Risk": {"Convey disapproval or negative judgment": {
      "id": "Convey disapproval or negative judgment",
      "name": "Convey disapproval or negative judgment",
      "reason": {
          "imperative": "convey disapproval or negative judgment",
          "declarative": "conveys disapproval or negative judgment",
          "gerund": "Conveying disapproval or negative judgment"
      },
      "explanation": {
          "markdown": "When we disapprove of patient distress or thoughts of suicide, we shame them and convey that it is not acceptable to have these thoughs or to reveal them to others."
      },
      "skill": "Assessing Suicide Risk",
      "quality": -1,
      "_id": "32d6632e-49fa-410b-adf9-72081cd66104",
      "_rev": "1-9cc8030ed34334da6868680428fc448e"
  },
  "Lead patient toward denying suicidal thoughts": {
      "id": "Lead patient toward denying suicidal thoughts",
      "name": "Lead patient toward denying suicidal thoughts",
      "reason": {
          "imperative": "lead patient toward denying suicidal thoughts",
          "declarative": "leads patient toward denying suicidal thoughts",
          "gerund": "Leading patient toward denying suicidal thoughts"
      },
      "explanation": {
          "markdown": "When we ask questions that lead patients toward denying distress or suicidal thoughts, we convey that we do not want to hear about their experience and they will be less likely to share their distress with us."
      },
      "skill": "Assessing Suicide Risk",
      "quality": -1,
      "_id": "476287c0-e924-4bb8-811a-98a7a930236a",
      "_rev": "1-dc82cb0843a7fbf17e39b663f52a0539"
  },
  "Skip ahead": {
      "id": "Skip ahead",
      "name": "Skip ahead",
      "reason": {
          "imperative": "skip ahead or move on prematurely before completing the suicide risk assessment in its prescribed order",
          "declarative": "skips ahead or moves on prematurely before completing the suicide risk assessment in its prescribed order",
          "gerund": "Skipping ahead or moving on prematurely before completing the suicide risk assessment in its prescribed order"
      },
      "explanation": {
          "markdown": "We need to complete the full suicide risk assessment in order before skipping ahead to a later suicide risk question, or moving on to a new topic."
      },
      "skill": "Assessing Suicide Risk",
      "quality": -1,
      "_id": "5dfd96c5-0ed5-48a6-ba41-e32baf2a28e4",
      "_rev": "1-0097e47f3d98b0a0e21afed9eb94107d"
  },
  "Ask about life context and stresses": {
      "id": "Ask about life context and stresses",
      "name": "Ask about life context and stresses",
      "reason": {
          "imperative": "ask about life context and stresses",
          "declarative": "asks about life context and stresses",
          "gerund": "Asking about life context and stresses"
      },
      "explanation": {
          "markdown": "If patients report any thoughts of suicide in the past month, ask about life context and stresses that may be causing distress (e.g., recent losses, social isolation, financial problems, legal problems, relationship problems)."
      },
      "skill": "Assessing Suicide Risk",
      "quality": 1,
      "_id": "601b3b64-e3d4-4956-ac95-97b82d3f1ce5",
      "_rev": "1-bd8cbb262a9c250424ee7cf8b5b3c505"
  },
  "Do not frame questions around the past month": {
      "id": "Do not frame questions around the past month",
      "name": "Do not frame questions around the past month",
      "reason": {
          "imperative": "ask about suicidal thoughts, but do not frame questions around the past month",
          "declarative": "asks about suicidal thoughts, but does not frame questions around the past month",
          "gerund": "Asking about suicidal thoughts, but not framing questions around the past month"
      },
      "explanation": {
          "markdown": "While we wish to know about patient history of suicidal thoughts and attempts, framing questions around their experiences in the past month allows us to more accurately categorize current suicide risk level."
      },
      "skill": "Assessing Suicide Risk",
      "quality": -1,
      "_id": "7a7ead94-443e-44b7-98ca-b39881255aca",
      "_rev": "1-28e7dd0221bc6dc27adc7b3a8f7dfa86"
  },
  "Ask about thoughts of suicide in the past month": {
      "id": "Ask about thoughts of suicide in the past month",
      "name": "Ask about thoughts of suicide in the past month",
      "reason": {
          "imperative": "ask about thoughts of suicide in the past month",
          "declarative": "asks about thoughts of suicide in the past month",
          "gerund": "Asking about thoughts of suicide in the past month"
      },
      "explanation": {
          "markdown": "Ask patients about thoughts of suicide or ending their lives in the past month, even if they deny wishes to be dead."
      },
      "skill": "Assessing Suicide Risk",
      "quality": 1,
      "_id": "953a0294-7dec-42a1-8e0d-7ba42201fb3f",
      "_rev": "1-aaf3d644d7fbc88154824c5a54da6315"
  },
  "Minimize patient feelings or experience": {
      "id": "Minimize patient feelings or experience",
      "name": "Minimize patient feelings or experience",
      "reason": {
          "imperative": "minimize patient feelings or experience",
          "declarative": "minimizes patient feelings or experience",
          "gerund": "Minimizing patient feelings or experience"
      },
      "explanation": {
          "markdown": "We may wish to reassure patients that their feelings are not that severe, but minimizing their feelings or experiences will lead them to feel not understood and to not be open with you."
      },
      "skill": "Assessing Suicide Risk",
      "quality": -1,
      "_id": "96b436bd-2918-4d34-9c1d-7a4e970f3100",
      "_rev": "1-62c3d8c8d6f0c0d7f9daea214fb358b2"
  },
  "Ask about specific detailed plan and intention": {
      "id": "Ask about specific detailed plan and intention",
      "name": "Ask about specific detailed plan and intention",
      "reason": {
          "imperative": "ask about specific detalied plan and intention",
          "declarative": "asks about specific detalied plan and intention",
          "gerund": "Asking about specific detalied plan and intention"
      },
      "explanation": {
          "markdown": "If patients report any thoughts of suicide in the past month, ask whether they have made any specific detailed plans and have intention to carry out that plan in the near future."
      },
      "skill": "Assessing Suicide Risk",
      "quality": 1,
      "_id": "a72b88b5-9728-4534-94b4-a2c1e74bcf8d",
      "_rev": "1-f3fb41d2d94e0d308d1d13f8d1c3aa9d"
  },
  "Ask about reasons for living": {
      "id": "Ask about reasons for living",
      "name": "Ask about reasons for living",
      "reason": {
          "imperative": "ask about reasons for living",
          "declarative": "asks about reasons for living",
          "gerund": "Asking about reasons for living"
      },
      "explanation": {
          "markdown": "If patients report any thoughts of suicide in the past month, ask about reasons for living and any protective factors (e.g., belief in ability to resist suicidal thoughts)."
      },
      "skill": "Assessing Suicide Risk",
      "quality": 1,
      "_id": "a9200434-7aad-4e6e-883c-942311757d83",
      "_rev": "1-0e7b6e77d5d303117a71129571ae1796"
  },
  "Ask about wishes to be dead in past month": {
      "id": "Ask about wishes to be dead in past month",
      "name": "Ask about wishes to be dead in past month",
      "reason": {
          "imperative": "ask about wishes to be dead in past month",
          "declarative": "asks about wishes to be dead in past month",
          "gerund": "Asking about wishes to be dead in past month"
      },
      "explanation": {
          "markdown": "In screening for suicide risk, the first question concerns wishes to be dead or feeling that life is not worth living in the past month."
      },
      "skill": "Assessing Suicide Risk",
      "quality": 1,
      "_id": "bfd4a629-6c56-4ec2-a5a8-d16320b6dff4",
      "_rev": "1-b38659bceae41338579c3831089d8b1b"
  },
  "Ask about suicidal behavior and preparation": {
      "id": "Ask about suicidal behavior and preparation",
      "name": "Ask about suicidal behavior and preparation",
      "reason": {
          "imperative": "ask about suicidal behavior and preparation",
          "declarative": "asks about suicidal behavior and preparation",
          "gerund": "Asking about suicidal behavior and preparation"
      },
      "explanation": {
          "markdown": "If patients report any thoughts of suicide in the past month, ask whether they have ever made a suicide attempt and whether they have recently made any preparations to die by suicide."
      },
      "skill": "Assessing Suicide Risk",
      "quality": 1,
      "_id": "caa0cc7d-17f3-4d6f-8c15-9e8b24c558f5",
      "_rev": "1-4e48a01b281966136a17b9c0844a30a2"
  },
  "Categorize suicide risk level": {
      "id": "Categorize suicide risk level",
      "name": "Categorize suicide risk level",
      "reason": {
          "imperative": "accurately categorize the suicide risk level of the patient",
          "declarative": "accurately categorizes the suicide risk level of the patient",
          "gerund": "Accurately categorizing the suicide risk level of the patient"
      },
      "explanation": {
          "markdown": "After asking all relevant questions, the provider then makes a judgment about current level of risk for suicide."
      },
      "skill": "Assessing Suicide Risk",
      "quality": 1,
      "_id": "de6ef933-3fcc-41e4-b634-726bfeae98c9",
      "_rev": "1-d69b45141f06e164b476994b1238d591"
  },
  "Seek out supervisor without completing assessment": {
      "id": "Seek out supervisor without completing assessment",
      "name": "Seek out supervisor without completing assessment",
      "reason": {
          "imperative": "seek out supervisor without completing assessment",
          "declarative": "seeks out supervisor without completing assessment",
          "gerund": "Seeking out supervisor without completing assessment"
      },
      "explanation": {
          "markdown": "While in some cases, a supervisor if available may be brought in to speak with the patient, the provider should conduct a full suicide risk assessment first."
      },
      "skill": "Assessing Suicide Risk",
      "quality": -1,
      "_id": "ea4ba249-2fe7-475f-b5c4-0363243b59c9",
      "_rev": "1-5e0d13153e47268baea3437d18a5f02c"
  },
  "Do not frame questions for both lifetime and past 3 months": {
      "id": "Do not frame questions for both lifetime and past 3 months",
      "name": "Do not frame questions for both lifetime and past 3 months",
      "reason": {
          "imperative": "do not frame questions on suicidal behavior for both lifetime and past 3 months",
          "declarative": "does not frame questions on suicidal behavior for both lifetime and past 3 months",
          "gerund": "Not framing questions on suicidal behavior for both lifetime and past 3 months"
      },
      "explanation": {
          "markdown": "Framing questions about suicidal behavior must be done for both lifetime and in the past 3 months, in order to more accurately categorize current suicide risk level."
      },
      "skill": "Assessing Suicide Risk",
      "quality": -1,
      "_id": "efc06fb0-0873-401e-8e62-b079e0cee809",
      "_rev": "1-949b8df4bb0c9d2b9c92bbba50c24015"
  } 
}} as StatisticsTagsByCompetency

export const tourIdentifyExampleRawPageData: RawPageData = {
  "problemQuality": true,
  "problemType": "multiChoice",
  "type": "P1",
  "skill": {
    "id": "Assessing Suicide Risk",
    "name": "Assessing Suicide Risk",
    "group": "Suicide Risk",
    "doc": "",
    "content": {
      "text": "When patients endorse item 9 on the PHQ or when they report any thoughts of death or suicide, the provider must follow up with a series of questions.  These questions largely focus on suicidal thoughts in the past month, with some question focused on suicidal behavior over the patient's lifetime.   Question to determine suicide risk must be asked in a prescribed order."
    },
    "_id": "6d80d8f2-d0a3-4c20-8a05-4b1d9ce00ad0",
    "_rev": "1-c5a562b5e2f8c4e3376c6c84f42a1482"
  },
  "options": [
    {
      "correct": true,
      "id": "Ask about specific detailed plan and intention",
      "text": {
        "imperative": "ask about specific detalied plan and intention",
        "declarative": "asks about specific detalied plan and intention",
        "gerund": "Asking about specific detalied plan and intention"
      },
      "name": "Ask about specific detailed plan and intention",
      "reason": [
        {
          "declarative": "If patients report any thoughts of suicide in the past month, ask whether they have made any specific detailed plans and have intention to carry out that plan in the near future.",
          "imperative": "If patients report any thoughts of suicide in the past month, ask whether they have made any specific detailed plans and have intention to carry out that plan in the near future.",
          "gerund": "If patients report any thoughts of suicide in the past month, ask whether they have made any specific detailed plans and have intention to carry out that plan in the near future."
        }
      ],
      "quality": 1,
      "tagData": [
        {
          "id": "Ask about specific detailed plan and intention",
          "name": "Ask about specific detailed plan and intention"
        }
      ],
      "selected": true
    },
    {
      "correct": true,
      "id": "Ask about wishes to be dead in past month",
      "text": {
        "imperative": "ask about wishes to be dead in past month",
        "declarative": "asks about wishes to be dead in past month",
        "gerund": "Asking about wishes to be dead in past month"
      },
      "name": "Ask about wishes to be dead in past month",
      "reason": [
        {
          "declarative": "In screening for suicide risk, the first question concerns wishes to be dead or feeling that life is not worth living in the past month.",
          "imperative": "In screening for suicide risk, the first question concerns wishes to be dead or feeling that life is not worth living in the past month.",
          "gerund": "In screening for suicide risk, the first question concerns wishes to be dead or feeling that life is not worth living in the past month."
        }
      ],
      "quality": 1,
      "tagData": [
        {
          "id": "Ask about wishes to be dead in past month",
          "name": "Ask about wishes to be dead in past month"
        }
      ],
      "selected": false
    },
    {
      "correct": true,
      "id": "Ask about thoughts of suicide in the past month",
      "text": {
        "imperative": "ask about thoughts of suicide in the past month",
        "declarative": "asks about thoughts of suicide in the past month",
        "gerund": "Asking about thoughts of suicide in the past month"
      },
      "name": "Ask about thoughts of suicide in the past month",
      "reason": [
        {
          "declarative": "Ask patients about thoughts of suicide or ending their lives in the past month, even if they deny wishes to be dead.",
          "imperative": "Ask patients about thoughts of suicide or ending their lives in the past month, even if they deny wishes to be dead.",
          "gerund": "Ask patients about thoughts of suicide or ending their lives in the past month, even if they deny wishes to be dead."
        }
      ],
      "quality": 1,
      "tagData": [
        {
          "id": "Ask about thoughts of suicide in the past month",
          "name": "Ask about thoughts of suicide in the past month"
        }
      ],
      "selected": false
    }
  ],
  "hints": [],
  "seed": 3,
  "ignoreList": [
    "Ask about specific detailed plan and intention",
    "Ask about wishes to be dead in past month",
    "Ask about thoughts of suicide in the past month"
  ],
  "playStats": {
    "userId": "test user",
    "sessionId": "096f61d9-8405-419d-844d-e0766478bdb7",
    "level": 1,
    "score": 0.3333333333333333,
    "events": [
      {
        "eventType": "problem load",
        "clientTime": 1670539747645
      },
      {
        "eventType": "select option",
        "eventData": {
          "id": "Ask about specific detailed plan and intention",
          "selected": false,
          "correctOption": true,
          "tags": [
            {
              "id": "Ask about specific detailed plan and intention",
              "name": "Ask about specific detailed plan and intention"
            }
          ]
        },
        "clientTime": 1670539757827
      },
      {
        "eventType": "first try wrong",
        "clientTime": 1670539760723
      },
      {
        "eventType": "select option",
        "eventData": {
          "id": "Ask about thoughts of suicide in the past month",
          "selected": false,
          "correctOption": true,
          "tags": [
            {
              "id": "Ask about thoughts of suicide in the past month",
              "name": "Ask about thoughts of suicide in the past month"
            }
          ]
        },
        "clientTime": 1670539763906
      },
      {
        "eventType": "select option",
        "eventData": {
          "id": "Ask about thoughts of suicide in the past month",
          "selected": true,
          "correctOption": true,
          "tags": [
            {
              "id": "Ask about thoughts of suicide in the past month",
              "name": "Ask about thoughts of suicide in the past month"
            }
          ]
        },
        "clientTime": 1670539765402
      },
      {
        "eventType": "problem submit",
        "eventData": {
          "problemQuality": true,
          "problemType": "multiChoice",
          "type": "P1",
          "skill": {
            "id": "Assessing Suicide Risk",
            "name": "Assessing Suicide Risk",
            "group": "Suicide Risk",
            "doc": "",
            "content": {
              "text": "When patients endorse item 9 on the PHQ or when they report any thoughts of death or suicide, the provider must follow up with a series of questions.  These questions largely focus on suicidal thoughts in the past month, with some question focused on suicidal behavior over the patient's lifetime.   Question to determine suicide risk must be asked in a prescribed order."
            },
            "_id": "6d80d8f2-d0a3-4c20-8a05-4b1d9ce00ad0",
            "_rev": "1-c5a562b5e2f8c4e3376c6c84f42a1482"
          },
          "options": [
            {
              "correct": true,
              "id": "Ask about specific detailed plan and intention",
              "text": {
                "imperative": "ask about specific detalied plan and intention",
                "declarative": "asks about specific detalied plan and intention",
                "gerund": "Asking about specific detalied plan and intention"
              },
              "name": "Ask about specific detailed plan and intention",
              "reason": [
                {
                  "declarative": "If patients report any thoughts of suicide in the past month, ask whether they have made any specific detailed plans and have intention to carry out that plan in the near future.",
                  "imperative": "If patients report any thoughts of suicide in the past month, ask whether they have made any specific detailed plans and have intention to carry out that plan in the near future.",
                  "gerund": "If patients report any thoughts of suicide in the past month, ask whether they have made any specific detailed plans and have intention to carry out that plan in the near future."
                }
              ],
              "quality": 1,
              "tagData": [
                {
                  "id": "Ask about specific detailed plan and intention",
                  "name": "Ask about specific detailed plan and intention"
                }
              ],
              "selected": true
            },
            {
              "correct": true,
              "id": "Ask about wishes to be dead in past month",
              "text": {
                "imperative": "ask about wishes to be dead in past month",
                "declarative": "asks about wishes to be dead in past month",
                "gerund": "Asking about wishes to be dead in past month"
              },
              "name": "Ask about wishes to be dead in past month",
              "reason": [
                {
                  "declarative": "In screening for suicide risk, the first question concerns wishes to be dead or feeling that life is not worth living in the past month.",
                  "imperative": "In screening for suicide risk, the first question concerns wishes to be dead or feeling that life is not worth living in the past month.",
                  "gerund": "In screening for suicide risk, the first question concerns wishes to be dead or feeling that life is not worth living in the past month."
                }
              ],
              "quality": 1,
              "tagData": [
                {
                  "id": "Ask about wishes to be dead in past month",
                  "name": "Ask about wishes to be dead in past month"
                }
              ],
              "selected": false
            },
            {
              "correct": true,
              "id": "Ask about thoughts of suicide in the past month",
              "text": {
                "imperative": "ask about thoughts of suicide in the past month",
                "declarative": "asks about thoughts of suicide in the past month",
                "gerund": "Asking about thoughts of suicide in the past month"
              },
              "name": "Ask about thoughts of suicide in the past month",
              "reason": [
                {
                  "declarative": "Ask patients about thoughts of suicide or ending their lives in the past month, even if they deny wishes to be dead.",
                  "imperative": "Ask patients about thoughts of suicide or ending their lives in the past month, even if they deny wishes to be dead.",
                  "gerund": "Ask patients about thoughts of suicide or ending their lives in the past month, even if they deny wishes to be dead."
                }
              ],
              "quality": 1,
              "tagData": [
                {
                  "id": "Ask about thoughts of suicide in the past month",
                  "name": "Ask about thoughts of suicide in the past month"
                }
              ],
              "selected": false
            }
          ],
          "hints": [],
          "seed": 3,
          "ignoreList": [
            "Ask about specific detailed plan and intention",
            "Ask about wishes to be dead in past month",
            "Ask about thoughts of suicide in the past month"
          ],
          "playStats": {
            "userId": "test user",
            "sessionId": "096f61d9-8405-419d-844d-e0766478bdb7",
            "level": 1,
            "score": 0.3333333333333333,
            "events": [
              {
                "eventType": "problem load",
                "clientTime": 1670539747645
              },
              {
                "eventType": "select option",
                "eventData": {
                  "id": "Ask about specific detailed plan and intention",
                  "selected": false,
                  "correctOption": true,
                  "tags": [
                    {
                      "id": "Ask about specific detailed plan and intention",
                      "name": "Ask about specific detailed plan and intention"
                    }
                  ]
                },
                "clientTime": 1670539757827
              },
              {
                "eventType": "first try wrong",
                "clientTime": 1670539760723
              },
              {
                "eventType": "select option",
                "eventData": {
                  "id": "Ask about thoughts of suicide in the past month",
                  "selected": false,
                  "correctOption": true,
                  "tags": [
                    {
                      "id": "Ask about thoughts of suicide in the past month",
                      "name": "Ask about thoughts of suicide in the past month"
                    }
                  ]
                },
                "clientTime": 1670539763906
              },
              {
                "eventType": "select option",
                "eventData": {
                  "id": "Ask about thoughts of suicide in the past month",
                  "selected": true,
                  "correctOption": true,
                  "tags": [
                    {
                      "id": "Ask about thoughts of suicide in the past month",
                      "name": "Ask about thoughts of suicide in the past month"
                    }
                  ]
                },
                "clientTime": 1670539765402
              },
              {
                "eventType": "problem submit",
                "clientTime": 1670539767853
              }
            ],
            "cumulativeTime": 17826,
            "tagResults": [
              {
                "tagId": "Ask about specific detailed plan and intention",
                "name": "Ask about specific detailed plan and intention",
                "correct": true
              },
              {
                "tagId": "Ask about wishes to be dead in past month",
                "name": "Ask about wishes to be dead in past month",
                "correct": false
              },
              {
                "tagId": "Ask about thoughts of suicide in the past month",
                "name": "Ask about thoughts of suicide in the past month",
                "correct": false
              }
            ],
            "progress": 0
          }
        },
        "clientTime": 1670539767853
      },
      {
        "eventType": "problem submit",
        "clientTime": 1670539769650
      }
    ],
    "cumulativeTime": 20277,
    "tagResults": [
      {
        "tagId": "Ask about specific detailed plan and intention",
        "name": "Ask about specific detailed plan and intention",
        "correct": true
      },
      {
        "tagId": "Ask about wishes to be dead in past month",
        "name": "Ask about wishes to be dead in past month",
        "correct": false
      },
      {
        "tagId": "Ask about thoughts of suicide in the past month",
        "name": "Ask about thoughts of suicide in the past month",
        "correct": false
      }
    ],
    "progress": 0
  }
} as RawPageData;

export const tourIdentifyExampleIssue: IssueCardElement = {
  skillId: 'Assessing Suicide Risk',
  tagId: "Ask about specific detailed plan and intention",
  level: 1,
  type: SFFProblemType.Type1,
  userMistakes: [{problemEventId: 'tourIdentifyExamplePageData', userId: 'student1'}],
  uniqueUsersWithMistake: 2
}

// TODO: save this as part of the initial redux state for stats
export const tourConfusionExampleRawPageData: RawPageData = {
    "problemQuality": false,
    "problemType": "singleChoice",
    "type": "P4",
    "skill": {
      "id": "Assessing Suicide Risk",
      "name": "Assessing Suicide Risk",
      "group": "Suicide Risk",
      "doc": "",
      "content": {
        "text": "When patients endorse item 9 on the PHQ or when they report any thoughts of death or suicide, the provider must follow up with a series of questions.  These questions largely focus on suicidal thoughts in the past month, with some question focused on suicidal behavior over the patient's lifetime.   Question to determine suicide risk must be asked in a prescribed order."
      },
      "_id": "6d80d8f2-d0a3-4c20-8a05-4b1d9ce00ad0",
      "_rev": "1-c5a562b5e2f8c4e3376c6c84f42a1482"
    },
    "options": [
      {
        "correct": true,
        "id": "Exemplar_Assessing Suicide Risk_61",
        "content": {
          "text": "I’ll need to bring in a clinician to do a risk assessment.",
          "slate": {
            "root": [
              {
                "type": "therapist",
                "children": [
                  {
                    "text": "I’ll need to bring in a clinician to do a risk assessment."
                  }
                ]
              }
            ]
          }
        },
        "quality": -1,
        "reason": [
          {
            "imperative": "seek out supervisor without completing assessment",
            "declarative": "seeks out supervisor without completing assessment",
            "gerund": "Seeking out supervisor without completing assessment"
          }
        ],
        "tagData": [
          {
            "id": "Seek out supervisor without completing assessment",
            "name": "Seek out supervisor without completing assessment"
          }
        ],
        "selected": false
      },
      {
        "correct": false,
        "id": "Exemplar_Assessing Suicide Risk_56",
        "content": {
          "text": "I’m sure it hasn’t gotten to the point where you would actually follow through with that, has it?",
          "slate": {
            "root": [
              {
                "type": "therapist",
                "children": [
                  {
                    "text": "I’m sure it hasn’t gotten to the point where you would actually follow through with that, has it?"
                  }
                ]
              }
            ]
          }
        },
        "quality": -1,
        "reason": [
          {
            "imperative": "lead patient toward denying suicidal thoughts",
            "declarative": "leads patient toward denying suicidal thoughts",
            "gerund": "Leading patient toward denying suicidal thoughts"
          }
        ],
        "tagData": [
          {
            "id": "Lead patient toward denying suicidal thoughts",
            "name": "Lead patient toward denying suicidal thoughts"
          }
        ],
        "selected": false
      },
      {
        "correct": false,
        "id": "Exemplar_Assessing Suicide Risk_62",
        "content": {
          "text": "Was there ever a point in your life where you intended to kill yourself?",
          "slate": {
            "root": [
              {
                "type": "therapist",
                "children": [
                  {
                    "text": "Was there ever a point in your life where you intended to kill yourself?"
                  }
                ]
              }
            ]
          }
        },
        "quality": -1,
        "reason": [
          {
            "imperative": "ask about suicidal thoughts, but do not frame questions around the past month",
            "declarative": "asks about suicidal thoughts, but does not frame questions around the past month",
            "gerund": "Asking about suicidal thoughts, but not framing questions around the past month"
          }
        ],
        "tagData": [
          {
            "id": "Do not frame questions around the past month",
            "name": "Do not frame questions around the past month"
          }
        ],
        "selected": true
      }
    ],
    "seed": 11,
    "scenario": {
      "id": "Scenario_91",
      "metadata": {
        "patient": {
          "name": "Bob",
          "background": "Bob is a 69-year-old White man. He was a former executive and was \"forced\" into retirement earlier than he wanted."
        },
        "difficulty": 0
      },
      "content": {
        "text": "When asked about thoughts of suicide in the past month, Bob states: “I’ve thought about taking all of my pills.”"
      },
      "name": "Scenario_91"
    },
    "ignoreList": [
      "Exemplar_Assessing Suicide Risk_61"
    ],
    "question": "seeks out supervisor without completing assessment",
    "playStats": {
      "userId": "test user",
      "sessionId": "096f61d9-8405-419d-844d-e0766478bdb7",
      "level": 4,
      "score": 0.3333333333333333,
      "events": [
        {
          "eventType": "problem load",
          "clientTime": 1670599229458
        },
        {
          "eventType": "select option",
          "eventData": {
            "id": "Exemplar_Assessing Suicide Risk_56",
            "selected": true,
            "correctOption": false,
            "tags": [
              {
                "id": "Lead patient toward denying suicidal thoughts",
                "name": "Lead patient toward denying suicidal thoughts"
              }
            ]
          },
          "clientTime": 1670599249156
        },
        {
          "eventType": "first try wrong",
          "clientTime": 1670599260012
        },
        {
          "eventType": "select option",
          "eventData": {
            "id": "Exemplar_Assessing Suicide Risk_56",
            "selected": false,
            "correctOption": false,
            "tags": [
              {
                "id": "Lead patient toward denying suicidal thoughts",
                "name": "Lead patient toward denying suicidal thoughts"
              }
            ]
          },
          "clientTime": 1670599261052
        },
        {
          "eventType": "select option",
          "eventData": {
            "id": "Exemplar_Assessing Suicide Risk_62",
            "selected": true,
            "correctOption": false,
            "tags": [
              {
                "id": "Do not frame questions around the past month",
                "name": "Do not frame questions around the past month"
              }
            ]
          },
          "clientTime": 1670599261053
        },
        {
          "eventType": "problem submit",
          "eventData": {
            "problemQuality": false,
            "problemType": "singleChoice",
            "type": "P4",
            "skill": {
              "id": "Assessing Suicide Risk",
              "name": "Assessing Suicide Risk",
              "group": "Suicide Risk",
              "doc": "",
              "content": {
                "text": "When patients endorse item 9 on the PHQ or when they report any thoughts of death or suicide, the provider must follow up with a series of questions.  These questions largely focus on suicidal thoughts in the past month, with some question focused on suicidal behavior over the patient's lifetime.   Question to determine suicide risk must be asked in a prescribed order."
              },
              "_id": "6d80d8f2-d0a3-4c20-8a05-4b1d9ce00ad0",
              "_rev": "1-c5a562b5e2f8c4e3376c6c84f42a1482"
            },
            "options": [
              {
                "correct": true,
                "id": "Exemplar_Assessing Suicide Risk_61",
                "content": {
                  "text": "I’ll need to bring in a clinician to do a risk assessment.",
                  "slate": {
                    "root": [
                      {
                        "type": "therapist",
                        "children": [
                          {
                            "text": "I’ll need to bring in a clinician to do a risk assessment."
                          }
                        ]
                      }
                    ]
                  }
                },
                "quality": -1,
                "reason": [
                  {
                    "imperative": "seek out supervisor without completing assessment",
                    "declarative": "seeks out supervisor without completing assessment",
                    "gerund": "Seeking out supervisor without completing assessment"
                  }
                ],
                "tagData": [
                  {
                    "id": "Seek out supervisor without completing assessment",
                    "name": "Seek out supervisor without completing assessment"
                  }
                ],
                "selected": false
              },
              {
                "correct": false,
                "id": "Exemplar_Assessing Suicide Risk_56",
                "content": {
                  "text": "I’m sure it hasn’t gotten to the point where you would actually follow through with that, has it?",
                  "slate": {
                    "root": [
                      {
                        "type": "therapist",
                        "children": [
                          {
                            "text": "I’m sure it hasn’t gotten to the point where you would actually follow through with that, has it?"
                          }
                        ]
                      }
                    ]
                  }
                },
                "quality": -1,
                "reason": [
                  {
                    "imperative": "lead patient toward denying suicidal thoughts",
                    "declarative": "leads patient toward denying suicidal thoughts",
                    "gerund": "Leading patient toward denying suicidal thoughts"
                  }
                ],
                "tagData": [
                  {
                    "id": "Lead patient toward denying suicidal thoughts",
                    "name": "Lead patient toward denying suicidal thoughts"
                  }
                ],
                "selected": false
              },
              {
                "correct": false,
                "id": "Exemplar_Assessing Suicide Risk_62",
                "content": {
                  "text": "Was there ever a point in your life where you intended to kill yourself?",
                  "slate": {
                    "root": [
                      {
                        "type": "therapist",
                        "children": [
                          {
                            "text": "Was there ever a point in your life where you intended to kill yourself?"
                          }
                        ]
                      }
                    ]
                  }
                },
                "quality": -1,
                "reason": [
                  {
                    "imperative": "ask about suicidal thoughts, but do not frame questions around the past month",
                    "declarative": "asks about suicidal thoughts, but does not frame questions around the past month",
                    "gerund": "Asking about suicidal thoughts, but not framing questions around the past month"
                  }
                ],
                "tagData": [
                  {
                    "id": "Do not frame questions around the past month",
                    "name": "Do not frame questions around the past month"
                  }
                ],
                "selected": true
              }
            ],
            "seed": 11,
            "scenario": {
              "id": "Scenario_91",
              "metadata": {
                "patient": {
                  "name": "Bob",
                  "background": "Bob is a 69-year-old White man. He was a former executive and was \"forced\" into retirement earlier than he wanted."
                },
                "difficulty": 0
              },
              "content": {
                "text": "When asked about thoughts of suicide in the past month, Bob states: “I’ve thought about taking all of my pills.”"
              },
              "name": "Scenario_91"
            },
            "ignoreList": [
              "Exemplar_Assessing Suicide Risk_61"
            ],
            "question": "seeks out supervisor without completing assessment",
            "playStats": {
              "userId": "test user",
              "sessionId": "096f61d9-8405-419d-844d-e0766478bdb7",
              "level": 4,
              "score": 0.3333333333333333,
              "events": [
                {
                  "eventType": "problem load",
                  "clientTime": 1670599229458
                },
                {
                  "eventType": "select option",
                  "eventData": {
                    "id": "Exemplar_Assessing Suicide Risk_56",
                    "selected": true,
                    "correctOption": false,
                    "tags": [
                      {
                        "id": "Lead patient toward denying suicidal thoughts",
                        "name": "Lead patient toward denying suicidal thoughts"
                      }
                    ]
                  },
                  "clientTime": 1670599249156
                },
                {
                  "eventType": "first try wrong",
                  "clientTime": 1670599260012
                },
                {
                  "eventType": "select option",
                  "eventData": {
                    "id": "Exemplar_Assessing Suicide Risk_56",
                    "selected": false,
                    "correctOption": false,
                    "tags": [
                      {
                        "id": "Lead patient toward denying suicidal thoughts",
                        "name": "Lead patient toward denying suicidal thoughts"
                      }
                    ]
                  },
                  "clientTime": 1670599261052
                },
                {
                  "eventType": "select option",
                  "eventData": {
                    "id": "Exemplar_Assessing Suicide Risk_62",
                    "selected": true,
                    "correctOption": false,
                    "tags": [
                      {
                        "id": "Do not frame questions around the past month",
                        "name": "Do not frame questions around the past month"
                      }
                    ]
                  },
                  "clientTime": 1670599261053
                },
                {
                  "eventType": "problem submit",
                  "clientTime": 1670599264558
                }
              ],
              "cumulativeTime": 31616,
              "tagResults": [
                {
                  "tagId": "Seek out supervisor without completing assessment",
                  "name": "Seek out supervisor without completing assessment",
                  "correct": false
                },
                {
                  "tagId": "Lead patient toward denying suicidal thoughts",
                  "name": "Lead patient toward denying suicidal thoughts",
                  "correct": true
                },
                {
                  "tagId": "Do not frame questions around the past month",
                  "name": "Do not frame questions around the past month",
                  "correct": false
                }
              ],
              "progress": 52.94117647058824
            }
          },
          "clientTime": 1670599264558
        },
        {
          "eventType": "problem submit",
          "clientTime": 1670599354637
        }
      ],
      "cumulativeTime": 35121,
      "tagResults": [
        {
          "tagId": "Seek out supervisor without completing assessment",
          "name": "Seek out supervisor without completing assessment",
          "correct": false
        },
        {
          "tagId": "Lead patient toward denying suicidal thoughts",
          "name": "Lead patient toward denying suicidal thoughts",
          "correct": true
        },
        {
          "tagId": "Do not frame questions around the past month",
          "name": "Do not frame questions around the past month",
          "correct": false
        }
      ],
      "progress": 52.94117647058824
    }
  } as RawPageData;

export const tourConfusionExample: ConfusionCardElement = {
  //key: "Seek out supervisor without completing assessment-Lead patient toward denying suicidal thoughts",
  skillId: 'Assessing Suicide Risk',
  tagAId: 'Lead patient toward denying suicidal thoughts',
  tagBId: 'Seek out supervisor without completing assessment',
  userMistakes: [{problemEventId: 'tourConfusionExamplePageData', userId: 'student1'}],
  count:2,
  failedCount:1,
}
